import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';

import { ENV } from "@app/constants/env";
import { getToken } from '@app/helpers/authProvider';
import {
  FinanceReportRequestType,
  ARInvoiceRptEntity,
  SupplierInvoicesRptEntity,
  EstimatesRptEntity,
} from '@feature/types/financereports.type';

const rawBaseQuery = fetchBaseQuery({
  baseUrl: ENV.BASE_API_URL + ENV.FINANCE_APIURL,
});

const authBaseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  incomingArgs,
  api,
  extraOptions
) => {
  let args: string | FetchArgs = incomingArgs;

  // normalize string arg to object so we can set headers
  if (typeof args === 'string') {
    args = { url: args };
  }

  try {
    const token = await getToken();
    if (token) {
      const headers = new Headers((args as FetchArgs).headers as HeadersInit | undefined);
      headers.set('Authorization', `Bearer ${token}`);
      (args as FetchArgs).headers = headers;
    }

    return await rawBaseQuery(args, api, extraOptions);
  } catch {
    return { error: { status: 500, data: 'Token retrieval failed' } } as any;
  }
};

export const financereportsapi = createApi({
  reducerPath: 'financereportsApi',
  baseQuery: authBaseQuery,
  tagTypes: ['financereports'],
  endpoints: (builder) => ({
    getARInvoiceReport: builder.mutation<ARInvoiceRptEntity[], FinanceReportRequestType>({
      query: (dto) => ({
        url: 'FinanceReports/GetARInvoiceReport',
        method: 'POST',
        body: dto,
      }),
    }),
    getSupplierInvoicesReport: builder.mutation<SupplierInvoicesRptEntity[], FinanceReportRequestType>({
      query: (dto) => ({
        url: 'FinanceReports/GetSupplierInvoicesReport',
        method: 'POST',
        body: dto,
      }),
    }),
    getWIPSalesReport: builder.mutation<EstimatesRptEntity[], FinanceReportRequestType>({
      query: (dto) => ({
        url: 'FinanceReports/GetWIPSalesReport',
        method: 'POST',
        body: dto,
      }),
    }),
    getCostAccrualReport: builder.mutation<EstimatesRptEntity[], FinanceReportRequestType>({
      query: (dto) => ({
        url: 'FinanceReports/GetCostAccrualReport',
        method: 'POST',
        body: dto,
      }),
    }),
  }),
});

export const {
  useGetARInvoiceReportMutation,
  useGetSupplierInvoicesReportMutation,
  useGetWIPSalesReportMutation,
  useGetCostAccrualReportMutation,
} = financereportsapi;
